/**
 * External Purchase Link Handler
 *
 * Handles opening Stripe Checkout in the appropriate way based on platform:
 * - Web: Redirects in the same window (checkout and the customer portal)
 * - iOS/Android: Opens in external browser (per Epic v. Apple/Google rulings)
 *
 * Legal Context (December 2025):
 * - iOS (US): Following Epic v. Apple, apps can use ExternalPurchaseLink API
 * - Android (US): Following Epic v. Google (Oct 2025), external payment links allowed
 */

import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';
import store from '@/store/store';
import { markCheckoutAttempted } from '@/store/slices/subscriptionSlice';

// OneLink domain for deep linking
const ONELINK_DOMAIN = import.meta.env.VITE_APPSFLYER_ONELINK_DOMAIN || 'paire.onelink.me';

/**
 * Open Stripe Checkout in the appropriate way based on platform.
 *
 * @param checkoutUrl - The Stripe Checkout URL
 */
export async function openExternalCheckout(checkoutUrl: string): Promise<void> {
  // D51f Phase 5: flag the trip BEFORE the platform branch so every real entry
  // point (the upgrade modal's and Subscription settings' own `handleUpgrade`)
  // converges here. Harmless on web (nothing listens there: the page is
  // replaced by Stripe and the return is a real navigation).
  store.dispatch(markCheckoutAttempted());

  const platform = Capacitor.getPlatform();

  if (platform === 'web') {
    // Redirect in same window for web
    window.location.href = checkoutUrl;
  } else {
    // iOS/Android: Open in external browser
    // StoreKit/Play Billing will show system disclosure automatically
    await Browser.open({
      url: checkoutUrl,
      presentationStyle: 'popover', // Use popover for better UX
    });
  }
}

/**
 * Generate a OneLink URL for mobile deep linking.
 * This enables deferred deep linking for users who may not have the app installed.
 *
 * @param deepLinkValue - The deep_link_value parameter for routing
 * @param campaign - Attribution campaign name
 * @returns OneLink URL
 */
function buildOneLinkUrl(deepLinkValue: string, campaign: string = 'subscription'): string {
  const params = new URLSearchParams({
    pid: 'paire_app',
    deep_link_value: deepLinkValue,
    c: campaign,
    af_dp: `paire://${deepLinkValue.replace('_', '/')}`,
  });
  return `https://${ONELINK_DOMAIN}?${params.toString()}`;
}

/**
 * Generate return URLs for Stripe Checkout based on platform.
 *
 * @param success - Whether this is for success or cancel flow
 * @returns The appropriate return URL
 */
export function getCheckoutReturnUrl(success: boolean): string {
  const platform = Capacitor.getPlatform();
  const baseUrl = import.meta.env.VITE_APP_URL || 'https://app.paire.io';

  if (platform === 'web') {
    return success
      ? `${baseUrl}/settings/subscription?success=true`
      : `${baseUrl}/settings/subscription?canceled=true`;
  } else {
    // Use OneLink for mobile apps to enable deferred deep linking
    // and proper attribution tracking through AppsFlyer
    return success
      ? buildOneLinkUrl('subscription_success', 'subscription')
      : buildOneLinkUrl('subscription_canceled', 'subscription');
  }
}

/**
 * Open Stripe Customer Portal for managing subscription.
 *
 * @param portalUrl - The Stripe Customer Portal URL
 */
export async function openCustomerPortal(portalUrl: string): Promise<void> {
  // D51f Phase 5: same flag as checkout — a member can change plan or cancel
  // in the portal, and the native foreground refresh must cover that trip too.
  store.dispatch(markCheckoutAttempted());

  const platform = Capacitor.getPlatform();

  if (platform === 'web') {
    // Same window, like checkout: the portal's `return_url` (`?portal_return=true`) must land in THIS tab, where
    // the stale entitlements are and where `SubscriptionSettings`' return-param effect runs. A new tab would load
    // the return cold and leave this one stale for up to the 5-minute stale time. (The member leaves the app tab
    // for Stripe, exactly as they do for checkout.)
    window.location.href = portalUrl;
  } else {
    // iOS/Android: Open in external browser
    await Browser.open({
      url: portalUrl,
      presentationStyle: 'popover',
    });
  }
}

/**
 * Check if the app is running on a mobile platform.
 */
export function isMobilePlatform(): boolean {
  const platform = Capacitor.getPlatform();
  return platform === 'ios' || platform === 'android';
}
